import { useLocation } from "react-router-dom";

import { chain } from "lodash";
import moment from "moment";

import { DEFAULT_LOCALE, SWR_NUMBER_REGEX } from "./constants";

export function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function groupBy(list, keyGetter) {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

function floatPrecision(floatValue, precision) {
  floatValue = parseFloat(floatValue);
  if (isNaN(floatValue)) {
    return parseFloat("0").toFixed(precision);
  } else {
    const power = Math.pow(10, precision);
    floatValue = (Math.round(floatValue * power) / power).toFixed(precision);
    return floatValue.toString();
  }
}

export function fileSize(size) {
  if (size >= 1024) {
    const kbSize = size / 1024;
    if (kbSize >= 1024) {
      const mbSize = kbSize / 1024;
      if (mbSize >= 1024) {
        const gbSize = mbSize / 1024;
        return `${floatPrecision(gbSize, 2)} GB`;
      }
      return `${Math.round(mbSize)} MB`;
    }
    return `${Math.round(kbSize)} kB`;
  }
  return `${size} B`;
}

export function latestSummary(list) {
  return list
    .filter((item) => item.fileName.includes("Data-"))
    .sort()
    .reverse()[0];
}

export function softwareVersion(list) {
  return list
    .filter((item) => item.fileName.includes("software_version.json"))
    .sort()
    .reverse()[0];
}

export function diskSpace(list) {
  return list
    .filter((item) => item.fileName.includes("disk_space.json"))
    .sort()
    .reverse()[0];
}

export function currentFiles(list) {
  return list
    .filter((item) => item.fileName.includes("current_files.json"))
    .sort()
    .reverse()[0];
}

export function novHealthLog(list) {
  return list
    .filter((item) => item.fileName.includes("nh.log"))
    .sort()
    .reverse()[0];
}

export function novDataLogs(list) {
  return list
    .filter((item) => item.fileName.includes(".tsv"))
    .sort()
    .reverse();
}

export function novEyeLogs(list) {
  return list
    .filter((item) => item.fileName.includes(".log"))
    .sort()
    .reverse();
}

export function novEyeVideos(list) {
  return list
    .filter((item) => item.fileName.includes(".mp4"))
    .sort((a, b) => {
      if (a.uploadedAt > b.uploadedAt) return 1;
      if (b.uploadedAt > a.uploadedAt) return -1;
      return 0;
    })
    .reverse();
}

export function novDataDailyReport(list) {
  return list
    .filter((item) => item.fileName.includes(".pdf"))
    .sort()
    .reverse();
}

// AYYMMDD.txt
// CYYMMDD.txt
export function novPLCAlarms(list) {
  return list
    .filter((item) => item.fileName.toLowerCase().includes(".txt"))
    .sort()
    .reverse();
}

export function getPLCAlarms(list, id) {
  return chain(list)
    .flatMap()
    .uniq()
    .filter((item) => item)
    .map((item) => {
      let obj = {};
      item.split(";").forEach((value, index) => {
        const key = PLC_COLUMN[index].name;

        obj = {
          ...obj,
          [key]: [2, 3, 4].includes(index) ? value.substring(2) : value,
        };
      });

      return {
        ...obj,
        _raw: item,
        unit: id && `SWR-${id === 1 ? 171 : id}`,
        type: item.toLowerCase().includes("error") ? "error" : "warning",
      };
    })
    .value();
}

export function getNDaysPLC(list, days = 30) {
  const today = moment().format("YYYY-MM-DD");
  // const last30Days = moment.rangeFromInterval("day", -30, today);
  const lastNDays = moment(today).subtract(days, "days").format("YYYY-MM-DD");
  return list
    .filter((item) => item.fileName.toLowerCase().includes(".txt"))
    .sort()
    .reverse()
    .map((item) => {
      const dateString = item.fileName.replace(/^\D+|\D+$/g, "");
      const date = moment(dateString, "YYMMDD").format("YYYY-MM-DD");

      return { ...item, date: date };
    })
    .filter((item) => {
      return moment(item.date).isBetween(lastNDays, today);
    });
}

// Alarm Status ; Time Received ; Time cleared ; Time Acknowledged ; Variable Name ; Value ; Measuring unit ; text ; User - Full name ; Computer name ; Comment
export function parsePLCAlarms(message) {
  return message
    .split("\n")
    .reverse()
    .filter((item) => item);
}

// Alarm Status ; Time Received ; Time cleared ; Time Acknowledged ; Variable Name ; Value ; Measuring unit ; text ; User - Full name ; Computer name ; Comment
export const PLC_COLUMN = [
  { name: "unit", label: "Unit" },
  { name: "message", label: "Alarm Status" },
  { name: "received_at", label: "Time Received" },
  { name: "cleared_at", label: "Time Cleared" },
  { name: "acknowledged_at", label: "Time Acknowledged" },
  { name: "variable_name", label: "Variable Name" },
  { name: "value", label: "Value" },
  // { name: 'measuring_unit', label: "Measuring Unit" },
  { name: "operator_name", label: "User - Full name" },
  { name: "computer_name", label: "Computer name" },
  { name: "comment", label: "Comment" },
];

export function convertInchesToCm(inches) {
  return Number(inches / 0.3937);
}

Date.prototype.addDays = function (days) {
  const date = new Date(this.valueOf());
  date.setDate(date.getDate() + days);
  return date;
};

function getDates(startDate, stopDate) {
  const dateArray = new Array();
  let currentDate = startDate;
  while (currentDate <= stopDate) {
    dateArray.push({
      date: currentDate.toLocaleDateString(DEFAULT_LOCALE),
    });
    currentDate = currentDate.addDays(1);
  }
  return dateArray;
}

export const getDateList = (start, end) => {
  return getDates(new Date(start), new Date(end));
};

export const getWrikeDataList = (array) => {
  return array
    .map((item, index) => {
      const unitNumber = [...item.title.matchAll(SWR_NUMBER_REGEX)][0] || [];

      return {
        ...item,
        key: index,
        unit: unitNumber[1],
        date: new Date(item.createdDate),
      };
    })
    .slice()
    .sort((a, b) => {
      if (new Date(a.createdDate) < new Date(b.createdDate)) {
        return 1;
      }
      if (new Date(a.createdDate) > new Date(b.createdDate)) {
        return -1;
      }
      return 0;
    });
};

export const dataOffsetDate = (lastOnline, today, selected) => {
  if (moment(lastOnline).isBetween(selected, today)) {
    return lastOnline;
  } else {
    return selected;
  }
};

// Decoding Novarc Pipe schedule numeric encoding back to alphabetical
export const getPipeSchedule = (schedule) => {
  switch (schedule) {
    case 1:
      return "STD";
    case 2:
      return "XH";
    case 3:
      return "XXH";
    case 4:
      return "XS";
    case 5:
      return "XXS";

    default:
      return schedule;
  }
};

/**
 * Returns date string in format of ['YYYY-MM-DD', 'YYYY-MM-DD'] from input passed
 * from DatePicker / RangePicker based on the type
 * @param {string} type
 * @param {moment} value
 * @returns Array of date range as ['YYYY-MM-DD', 'YYYY-MM-DD']
 */
export function GetDateRange(type, value) {
  if (Array.isArray(value))
    switch (type) {
      case "date":
        return [value[0].format("YYYY-MM-DD"), value[0].format("YYYY-MM-DD")];

      case "week":
      case "month":
      case "quarter":
      case "year":
        return [
          value[0].startOf(type).format("YYYY-MM-DD"),
          value[1].endOf(type).format("YYYY-MM-DD"),
        ];

      default:
        return [value[0].format("YYYY-MM-DD"), value[1].format("YYYY-MM-DD")];
    }
  else {
    switch (type) {
      case "date":
        return [value.format("YYYY-MM-DD"), value.format("YYYY-MM-DD")];

      case "week":
      case "month":
      case "quarter":
      case "year":
        return [
          value.startOf(type).format("YYYY-MM-DD"),
          value.endOf(type).format("YYYY-MM-DD"),
        ];

      default:
        return [value.format("YYYY-MM-DD"), value.format("YYYY-MM-DD")];
    }
  }
}
